import { Injectable } from '@angular/core';
import {
  SwUpdate,
  VersionEvent,
  VersionReadyEvent,
  VersionInstallationFailedEvent,
} from '@angular/service-worker';
import { filter } from 'rxjs/operators';
import { AlertService, AlertTypeEnum } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private readonly dismissedHashSessionKey = 'kaparking:pwa_update_dismissed_hash';
  private initialized = false;

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly alerts: AlertService,
  ) {}

  /** Call once during app startup. Safe to call multiple times. */
  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    if (!this.swUpdate.isEnabled) {
      return;
    }

    // Periodic update checks (best-effort)
    setTimeout(() => {
      this.swUpdate.checkForUpdate().catch(() => undefined);
    }, 60_000);

    setInterval(() => {
      this.swUpdate.checkForUpdate().catch(() => undefined);
    }, 6 * 60 * 60 * 1000);

    this.swUpdate.versionUpdates
      .pipe(filter((e: VersionEvent): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe((event) => {
        void this.promptForUpdate(event.latestVersion.hash);
      });

    this.swUpdate.versionUpdates
      .pipe(
        filter(
          (e: VersionEvent): e is VersionInstallationFailedEvent =>
            e.type === 'VERSION_INSTALLATION_FAILED',
        ),
      )
      .subscribe(() => {
        this.alerts.create({
          type: AlertTypeEnum.WARNING,
          title: 'Update failed',
          message: 'A new version was found but failed to install. Please try again later.',
          timeout: 0,
          showConfirmButton: true,
        });
      });

    this.swUpdate.unrecoverable.subscribe(() => {
      void this.alerts.confirm({
        title: 'App needs reload',
        message: 'An unexpected error occurred. Reload to restore the application.',
        confirmText: 'Reload',
        cancelText: 'Later',
      }).then((reload) => {
        if (reload) {
          document.location.reload();
        }
      });
    });
  }

  private async promptForUpdate(latestHash: string): Promise<void> {
    const dismissedHash = sessionStorage.getItem(this.dismissedHashSessionKey);
    if (dismissedHash === latestHash) return;

    const shouldUpdateNow = await this.alerts.confirm({
      title: 'Update available',
      message: 'A newer version of Kaparking is available. Update now?',
      confirmText: 'Update now',
      cancelText: 'Later',
    });

    if (!shouldUpdateNow) {
      sessionStorage.setItem(this.dismissedHashSessionKey, latestHash);
      return;
    }

    try {
      await this.swUpdate.activateUpdate();
    } finally {
      document.location.reload();
    }
  }
}
